import React, { useState, useEffect } from 'react';
import axios from 'axios';
import Layout from '@/components/Layout';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import VideoTutorialButton from '../components/VideoTutorialButton';
import { downloadAllClassBackups } from '../utils/downloadClassBackups';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { BookOpen, Plus, Users, AlertCircle, Download, Trash2, Pencil, FileText, X, ClipboardList, TrendingUp, Bell, Megaphone, Copy } from 'lucide-react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import LastUpdated from '@/components/LastUpdated';
import firstName from '@/utils/firstName';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ClassesPage = ({ user, onLogout }) => {
  const [classes, setClasses] = useState([]);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [refreshing, setRefreshing] = useState(false);
  const [students, setStudents] = useState([]);
  const [availableGroups, setAvailableGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [studentDialogOpen, setStudentDialogOpen] = useState(false);
  const [formData, setFormData] = useState({ subject: '', group_id: '', group: '', description: '', turno: '' });
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editForm, setEditForm] = useState({ id: '', subject: '', turno: '' });
  const [studentFormData, setStudentFormData] = useState({ name: '' });
  const [encuadreDialog, setEncuadreDialog] = useState(false);
  const [selectedClass, setSelectedClass] = useState(null);
  const [encuadreMode, setEncuadreMode] = useState('choice');
  const [manualCriterios, setManualCriterios] = useState([]);
  const [critNombre, setCritNombre] = useState('');
  const [critPeso, setCritPeso] = useState('');
  const [savingManual, setSavingManual] = useState(false);
  const [reuseSaving, setReuseSaving] = useState(false);
  const [downloadingAll, setDownloadingAll] = useState(false);
  const [stats, setStats] = useState({ attendance_percentage: 0, active_tasks: 0 });
  const [adminNotifications, setAdminNotifications] = useState([]);
  const [currentAdminNotifIndex, setCurrentAdminNotifIndex] = useState(0);
  const [jefeNotifications, setJefeNotifications] = useState([]);
  const navigate = useNavigate();

  useEffect(() => {
    fetchClasses();
    fetchStudents();
    fetchAvailableGroups();
    fetchStats();
    fetchAdminNotifications();
    fetchJefeNotifications();
  }, []);

  // Auto-open the "Crear Nueva Clase" form when arriving with ?create=1
  // (e.g. from the teacher Dashboard "Crear Primera Clase" button).
  const [searchParams, setSearchParams] = useSearchParams();
  useEffect(() => {
    if (searchParams.get('create') === '1') {
      setDialogOpen(true);
      searchParams.delete('create');
      setSearchParams(searchParams, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  const fetchClasses = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/classes`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setClasses(response.data);
      setLastUpdated(Date.now());
    } catch (error) {
      console.error('Error fetching classes:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    try {
      await Promise.all([fetchClasses(), fetchStats(), fetchAdminNotifications(), fetchJefeNotifications()]);
    } finally {
      setRefreshing(false);
    }
  };

  const fetchStats = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/teacher/stats`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setStats(response.data);
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  };

  const fetchAdminNotifications = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/teacher/notifications-admin`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAdminNotifications(response.data || []);
    } catch (error) {
      console.error('Error fetching admin notifications:', error);
    }
  };

  const fetchJefeNotifications = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/teacher/notifications-jefe`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setJefeNotifications(response.data || []);
    } catch (error) {
      console.error('Error fetching jefe notifications:', error);
    }
  };

  const dismissJefeNotif = async (notifId) => {
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/teacher/notifications-jefe/${notifId}/read`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setJefeNotifications(prev => prev.filter(n => n.id !== notifId));
    } catch (error) {
      console.error('Error:', error);
    }
  };

  const markAdminNotifRead = async () => {
    const notif = adminNotifications[currentAdminNotifIndex];
    if (!notif) return;
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/teacher/notifications-admin/${notif.id}/read`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (currentAdminNotifIndex < adminNotifications.length - 1) {
        setCurrentAdminNotifIndex(prev => prev + 1);
      } else {
        setAdminNotifications([]);
        setCurrentAdminNotifIndex(0);
      }
    } catch (error) {
      console.error('Error marking notification as read:', error);
    }
  };

  const fetchStudents = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/students`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setStudents(response.data);
    } catch (error) {
      console.error('Error fetching students:', error);
    }
  };

  const fetchAvailableGroups = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/teacher/available-groups`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAvailableGroups(response.data);
    } catch (error) {
      console.error('Error fetching available groups:', error);
    }
  };

  const handleGroupSelect = (groupId) => {
    const selectedGroup = availableGroups.find(g => g.id === groupId);
    setFormData({ 
      ...formData, 
      group_id: groupId, 
      group: selectedGroup ? selectedGroup.name : '' 
    });
  };

  const handleCreateClass = async (e) => {
    e.preventDefault();
    if (!formData.group_id) {
      toast.error('Debes seleccionar un grupo');
      return;
    }
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/classes`, formData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('¡Clase creada exitosamente!');
      setDialogOpen(false);
      setFormData({ subject: '', group_id: '', group: '', description: '', turno: '' });
      fetchClasses();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al crear la clase');
    }
  };

  const openEditClass = (classItem) => {
    setEditForm({ id: classItem.id, subject: classItem.subject || '', turno: classItem.turno || '' });
    setEditDialogOpen(true);
  };

  const handleUpdateClass = async (e) => {
    e.preventDefault();
    if (!editForm.subject.trim()) {
      toast.error('El nombre de la clase es obligatorio');
      return;
    }
    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API}/classes/${editForm.id}`, {
        subject: editForm.subject.trim(),
        turno: editForm.turno || null
      }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Clase actualizada');
      setEditDialogOpen(false);
      fetchClasses();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al actualizar la clase');
    }
  };


  const handleCreateStudent = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/students`, studentFormData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('¡Alumno creado exitosamente!');
      setStudentDialogOpen(false);
      setStudentFormData({ name: '' });
      fetchStudents();
    } catch (error) {
      toast.error('Error al crear el alumno');
    }
  };

  const handleDownloadAllBackups = async () => {
    if (classes.length === 0) { toast.error('No hay clases para descargar'); return; }
    if (!window.confirm(`Se descargarán ${classes.length} archivo(s), uno por cada clase. ¿Continuar?`)) return;
    setDownloadingAll(true);
    try {
      await downloadAllClassBackups(classes);
    } finally {
      setDownloadingAll(false);
    }
  };

  const downloadClassExcel = async (e, classItem) => {
    e.stopPropagation();
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/teacher/classes/${classItem.id}/export-excel`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${classItem.subject}_${classItem.group}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Reporte descargado');
    } catch (error) {
      toast.error('Error al descargar el reporte');
    }
  };

  const handleEncuadreClick = (e, classItem) => {
    e.stopPropagation();
    setSelectedClass(classItem);
    setCritNombre('');
    setCritPeso('');
    if (classItem.encuadre_manual && classItem.encuadre_manual.length > 0) {
      setManualCriterios(classItem.encuadre_manual.map(c => ({ nombre: c.nombre, peso: c.peso })));
      setEncuadreMode('manual');
    } else {
      setManualCriterios([]);
      setEncuadreMode('choice');
    }
    setEncuadreDialog(true);
  };

  const chooseManual = () => {
    if (selectedClass?.encuadre_manual?.length) {
      setManualCriterios(selectedClass.encuadre_manual.map(c => ({ nombre: c.nombre, peso: c.peso })));
    } else {
      setManualCriterios([]);
    }
    setEncuadreMode('manual');
  };

  const addCriterio = () => {
    const nombre = critNombre.trim();
    if (!nombre) { toast.error('Escribe el nombre del criterio'); return; }
    setManualCriterios(prev => [...prev, { nombre, peso: critPeso.trim() }]);
    setCritNombre('');
    setCritPeso('');
  };

  const removeCriterio = (idx) => {
    setManualCriterios(prev => prev.filter((_, i) => i !== idx));
  };

  const saveManualEncuadre = async () => {
    if (!selectedClass?.id) return;
    if (manualCriterios.length === 0) { toast.error('Agrega al menos un criterio'); return; }
    const total = manualCriterios.reduce((s, c) => s + (parseFloat(String(c.peso).replace('%', '')) || 0), 0);
    if (total !== 100) {
      if (!window.confirm(`La suma de los pesos es ${total}%, no es 100%. ¿Deseas guardar de todos modos?`)) return;
    }
    setSavingManual(true);
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/classes/${selectedClass.id}/encuadre-manual`, { criterios: manualCriterios }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Encuadre guardado');
      setEncuadreDialog(false);
      fetchClasses();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al guardar el encuadre');
    } finally {
      setSavingManual(false);
    }
  };

  const deleteManualEncuadre = async () => {
    if (!selectedClass?.id) return;
    if (!window.confirm('¿Eliminar el encuadre manual?')) return;
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/classes/${selectedClass.id}/encuadre-manual`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Encuadre eliminado');
      setEncuadreDialog(false);
      fetchClasses();
    } catch (error) {
      toast.error('Error al eliminar el encuadre');
    }
  };

  // ---- Reutilizar Encuadre ----
  const _stripAccents = (s) => String(s || '').normalize('NFD').replace(/[\u0300-\u036f]/g, '');
  const _norm = (s) => _stripAccents(s).toLowerCase().trim().replace(/\s+/g, ' ');
  const _manualSignature = (criterios) =>
    'manual:' + JSON.stringify((criterios || []).map(c => `${_norm(c.nombre)}|${_norm(String(c.peso).replace('%', ''))}`).sort());

  // Build a deduped list of encuadres from the teacher's OTHER classes.
  const getReusableEncuadres = () => {
    const map = new Map(); // signature -> { type, criterios?, url?, origins: [subject] }
    (classes || []).forEach(c => {
      if (selectedClass && c.id === selectedClass.id) return; // exclude current class
      const label = `${c.subject}${c.turno ? ` (${c.turno})` : ''}`;
      if (c.encuadre_manual && c.encuadre_manual.length > 0) {
        const sig = _manualSignature(c.encuadre_manual);
        if (!map.has(sig)) {
          map.set(sig, { type: 'manual', criterios: c.encuadre_manual.map(x => ({ nombre: x.nombre, peso: x.peso })), origins: [] });
        }
        map.get(sig).origins.push(label);
      }
    });
    return Array.from(map.values());
  };

  const applyReuseManual = async (criterios) => {
    if (!selectedClass?.id) return;
    setReuseSaving(true);
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/classes/${selectedClass.id}/encuadre-manual`, { criterios }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Encuadre reutilizado');
      setEncuadreDialog(false);
      fetchClasses();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al reutilizar el encuadre');
    } finally {
      setReuseSaving(false);
    }
  };

  return (
    <Layout user={user} onLogout={onLogout}>
      <div className="p-6 md:p-8 lg:p-12">
        <div className="mb-8 flex items-center justify-between">
          <div>
            {firstName(user?.name) && (
              <p className="text-base text-slate-500 mb-1" data-testid="welcome-greeting">
                Bienvenido(a), <span className="font-semibold text-slate-700">{firstName(user?.name)}</span>
              </p>
            )}
            <h1 className="text-4xl md:text-5xl font-bold text-slate-900 tracking-tight mb-2" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }} data-testid="classes-page-title">
              Gestionar Clases
            </h1>
            <p className="text-base text-slate-600">Crea y administra tus clases</p>
            <div className="mt-2">
              <VideoTutorialButton videoUrl="https://youtu.be/TOu7sRtjXUo?si=RjCBuzOxojWbKY6b" />
            </div>
          </div>
          <div className="flex gap-3 items-center">
            <LastUpdated timestamp={lastUpdated} onRefresh={handleRefresh} refreshing={refreshing} />
            <Button
              onClick={handleDownloadAllBackups}
              disabled={downloadingAll || classes.length === 0}
              data-testid="download-all-backups-button"
              className="bg-emerald-500 hover:bg-emerald-600 text-white font-medium px-4 py-2.5 rounded-lg shadow-sm"
              title="Descargar respaldo de todas las clases (un archivo por clase)"
            >
              <Download className="w-4 h-4 mr-2" />
              {downloadingAll ? 'Descargando…' : 'Respaldo (todas)'}
            </Button>
            <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
              <DialogTrigger asChild>
                <Button data-testid="create-class-button" className="bg-orange-500 hover:bg-orange-600 text-white font-medium px-6 py-2.5 rounded-lg shadow-sm">
                  <Plus className="w-4 h-4 mr-2" />
                  Crear Clase
                </Button>
              </DialogTrigger>
              <DialogContent className="bg-white" aria-describedby="dialog-create-class-description">
                <DialogHeader>
                  <DialogTitle>Crear Nueva Clase</DialogTitle>
                </DialogHeader>
                <p id="dialog-create-class-description" className="text-sm text-slate-600 mb-4">
                  Ingresa los detalles de la nueva clase
                </p>
                <form onSubmit={handleCreateClass} className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="class-subject">Asignatura</Label>
                    <Input
                      id="class-subject"
                      data-testid="class-subject-input"
                      value={formData.subject}
                      onChange={(e) => setFormData({ ...formData, subject: e.target.value })}
                      required
                      placeholder="Ej: Matemáticas"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="class-group">Grupo</Label>
                    {availableGroups.length === 0 ? (
                      <div className="flex items-center gap-2 p-3 bg-amber-50 border border-amber-200 rounded-lg text-amber-800 text-sm">
                        <AlertCircle className="w-4 h-4" />
                        <span>No hay grupos disponibles. El administrador debe crear grupos primero.</span>
                      </div>
                    ) : (
                      <Select value={formData.group_id} onValueChange={handleGroupSelect}>
                        <SelectTrigger data-testid="class-group-select">
                          <SelectValue placeholder="Selecciona un grupo" />
                        </SelectTrigger>
                        <SelectContent>
                          {[...availableGroups].sort((a, b) => a.name.localeCompare(b.name, 'es')).map((group) => (
                            <SelectItem key={group.id} value={group.id}>
                              {group.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    )}
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="class-description">Descripción (opcional)</Label>
                    <Input
                      id="class-description"
                      data-testid="class-description-input"
                      value={formData.description}
                      onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                      placeholder="Descripción de la clase"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="class-turno">Turno</Label>
                    <Select value={formData.turno} onValueChange={(v) => setFormData({ ...formData, turno: v })}>
                      <SelectTrigger data-testid="class-turno-select">
                        <SelectValue placeholder="Selecciona el turno" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Matutino">Matutino</SelectItem>
                        <SelectItem value="Vespertino">Vespertino</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <Button 
                    type="submit" 
                    data-testid="submit-class-button" 
                    className="w-full bg-orange-500 hover:bg-orange-600"
                    disabled={availableGroups.length === 0 || !formData.group_id}
                  >
                    Crear Clase
                  </Button>
                </form>
              </DialogContent>
            </Dialog>
          </div>
        </div>

        {/* Tarjetas indicadoras (resumen del día) — migradas desde el antiguo Dashboard */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          {[
            { label: 'Total de Clases', value: classes.length, icon: BookOpen, color: 'bg-blue-500' },
            { label: 'Total de Alumnos', value: classes.reduce((acc, c) => acc + (c.student_ids?.length || 0), 0), icon: Users, color: 'bg-green-500' },
            { label: 'Tareas Activas', value: stats.active_tasks, icon: ClipboardList, color: 'bg-orange-500' },
            { label: 'Asistencia Promedio', value: `${stats.attendance_percentage}%`, icon: TrendingUp, color: 'bg-purple-500' },
          ].map((stat, index) => (
            <Card key={index} className="p-6 bg-white border border-slate-100 rounded-xl shadow-[0_2px_8px_rgba(0,0,0,0.04)] hover:shadow-md transition-all" data-testid={`stat-card-${index}`}>
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-600 mb-1">{stat.label}</p>
                  <p className="text-3xl font-bold text-slate-900">{stat.value}</p>
                </div>
                <div className={`${stat.color} w-12 h-12 rounded-lg flex items-center justify-center`}>
                  <stat.icon className="w-6 h-6 text-white" />
                </div>
              </div>
            </Card>
          ))}
        </div>

        {/* Jefe Docentes Notifications Board — migrado desde el antiguo Dashboard */}
        {jefeNotifications.length > 0 && (
          <div className="mb-6 space-y-2" data-testid="jefe-notifications-board">
            <h3 className="text-sm font-semibold text-slate-700 flex items-center gap-2">
              <Bell className="w-4 h-4 text-indigo-500" /> Avisos de Jefe de Docentes
            </h3>
            {jefeNotifications.map(n => (
              <Card key={n.id} className={`p-3 border-l-4 ${n.notification_type === 'citatorio' ? 'border-l-rose-500 bg-rose-50' : 'border-l-amber-500 bg-amber-50'}`}>
                <div className="flex items-start justify-between">
                  <div>
                    <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${n.notification_type === 'citatorio' ? 'bg-rose-100 text-rose-700' : 'bg-amber-100 text-amber-700'}`}>
                      {n.notification_type === 'citatorio' ? 'Citatorio' : 'Aviso'}
                    </span>
                    {n.motivo && <p className="text-sm mt-1"><strong>Motivo:</strong> {n.motivo}</p>}
                    {n.mensaje && <p className="text-sm mt-1">{n.mensaje}</p>}
                    {n.fecha && <p className="text-xs text-slate-600 mt-1">Fecha: {n.fecha} | Hora: {n.hora} | Lugar: {n.lugar}</p>}
                    <p className="text-xs text-slate-400 mt-1">De: {n.created_by_name}</p>
                  </div>
                  <Button size="sm" variant="ghost" onClick={() => dismissJefeNotif(n.id)} className="text-slate-500 hover:text-slate-700">Entendido</Button>
                </div>
              </Card>
            ))}
          </div>
        )}

        {loading ? (
          <div className="text-center py-12 text-slate-600">Cargando...</div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {classes.map((classItem) => (
              <Card
                key={classItem.id}
                className="p-6 bg-white border border-slate-100 rounded-xl shadow-[0_2px_8px_rgba(0,0,0,0.04)] hover:shadow-md transition-all"
                data-testid={`class-item-${classItem.id}`}
              >
                <div className="flex items-start justify-between mb-4">
                  <div className="bg-gradient-to-br from-orange-50 to-slate-100 w-12 h-12 rounded-lg flex items-center justify-center">
                    <BookOpen className="w-6 h-6 text-orange-500" />
                  </div>
                  <div className="flex items-center gap-2">
                    <Button
                      onClick={(e) => downloadClassExcel(e, classItem)}
                      size="sm"
                      className="bg-emerald-500 hover:bg-emerald-600 text-white text-xs px-2 py-1 h-auto"
                      data-testid={`classes-download-btn-${classItem.id}`}
                      title="Descargar reporte Excel"
                    >
                      <Download className="w-3 h-3 mr-1" />
                      Excel
                    </Button>
                    {classItem.encuadre_manual?.length > 0 ? (
                      <Button
                        onClick={(e) => handleEncuadreClick(e, classItem)}
                        size="sm"
                        className="bg-green-600 hover:bg-green-700 text-white text-xs px-2 py-1 h-auto"
                        data-testid={`classes-encuadre-btn-${classItem.id}`}
                        title="Ver/editar encuadre"
                      >
                        <FileText className="w-3 h-3 mr-1" />
                        Encuadre
                      </Button>
                    ) : (
                      <Button
                        onClick={(e) => handleEncuadreClick(e, classItem)}
                        size="sm"
                        className="bg-purple-500 hover:bg-purple-600 text-white text-xs px-2 py-1 h-auto"
                        data-testid={`classes-encuadre-btn-${classItem.id}`}
                      >
                        <FileText className="w-3 h-3 mr-1" />
                        Encuadre
                      </Button>
                    )}
                    <span className="inline-flex items-center rounded-full border border-slate-200 bg-slate-50 px-2.5 py-0.5 text-xs font-semibold text-slate-700">
                      {classItem.group}
                    </span>
                    {classItem.turno && (
                      <span className="inline-flex items-center rounded-full border border-indigo-200 bg-indigo-50 px-2.5 py-0.5 text-xs font-semibold text-indigo-700" data-testid={`class-turno-badge-${classItem.id}`}>
                        {classItem.turno}
                      </span>
                    )}
                  </div>
                </div>
                <h3 className="text-xl font-semibold text-slate-900 mb-2">{classItem.subject}</h3>
                <p className="text-sm text-slate-600 mb-4">{classItem.description || 'Sin descripción'}</p>
                
                <div className="flex items-center text-sm text-slate-600 mb-4">
                  <Users className="w-4 h-4 mr-1" />
                  {classItem.student_ids.length} alumnos
                </div>
                <Button
                  onClick={() => navigate(`/class/${classItem.id}/avance-programatico`)}
                  data-testid={`avance-programatico-${classItem.id}`}
                  className="w-full mb-2 bg-purple-600 hover:bg-purple-700 text-white text-sm py-2 rounded-lg flex items-center justify-center gap-2"
                  title="Registrar el tema visto por fecha"
                >
                  <ClipboardList className="w-4 h-4" /> Avance Programático
                </Button>
                <div className="flex gap-2">
                  <Button
                    onClick={() => navigate(`/class/${classItem.id}`)}
                    data-testid={`view-class-${classItem.id}`}
                    className="flex-1 bg-orange-500 hover:bg-orange-600 text-white text-sm py-2 rounded-lg"
                  >
                    Ver Clase
                  </Button>
                  <Button
                    onClick={() => openEditClass(classItem)}
                    data-testid={`edit-class-${classItem.id}`}
                    className="px-3 bg-slate-600 hover:bg-slate-700 text-white text-sm py-2 rounded-lg"
                    title="Editar nombre y turno"
                  >
                    <Pencil className="w-4 h-4" />
                  </Button>
                  <Button
                    onClick={async () => {
                      if (window.confirm('¿Estás seguro de eliminar esta clase? Se borrarán todos los datos.')) {
                        try {
                          const token = localStorage.getItem('token');
                          await axios.delete(`${API}/classes/${classItem.id}`, {
                            headers: { Authorization: `Bearer ${token}` }
                          });
                          toast.success('Clase eliminada');
                          fetchClasses();
                        } catch (error) {
                          toast.error('Error al eliminar clase');
                        }
                      }
                    }}
                    data-testid={`delete-class-${classItem.id}`}
                    className="px-3 bg-red-500 hover:bg-red-600 text-white text-sm py-2 rounded-lg"
                  >
                    🗑️
                  </Button>
                </div>
              </Card>
            ))}
          </div>
        )}
      </div>

      {/* Edit Class Dialog (name + turno) */}
      <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
        <DialogContent className="bg-white" aria-describedby="dialog-edit-class-description">
          <DialogHeader>
            <DialogTitle>Editar Clase</DialogTitle>
          </DialogHeader>
          <p id="dialog-edit-class-description" className="sr-only">Editar nombre y turno de la clase</p>
          <form onSubmit={handleUpdateClass} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="edit-class-subject">Asignatura</Label>
              <Input
                id="edit-class-subject"
                data-testid="edit-class-subject-input"
                value={editForm.subject}
                onChange={(e) => setEditForm({ ...editForm, subject: e.target.value })}
                required
                placeholder="Ej: Matemáticas"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="edit-class-turno">Turno</Label>
              <Select value={editForm.turno} onValueChange={(v) => setEditForm({ ...editForm, turno: v })}>
                <SelectTrigger data-testid="edit-class-turno-select">
                  <SelectValue placeholder="Selecciona el turno" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Matutino">Matutino</SelectItem>
                  <SelectItem value="Vespertino">Vespertino</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <Button type="submit" data-testid="submit-edit-class-button" className="w-full bg-orange-500 hover:bg-orange-600">
              Guardar Cambios
            </Button>
          </form>
        </DialogContent>
      </Dialog>


      {/* Encuadre Dialog (choice / manual / reuse) */}
      <Dialog open={encuadreDialog} onOpenChange={setEncuadreDialog}>
        <DialogContent className="bg-white max-w-2xl">
          <DialogHeader>
            <DialogTitle>Encuadre - {selectedClass?.subject}</DialogTitle>
          </DialogHeader>

          {encuadreMode === 'choice' && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 py-2">
              <button
                onClick={chooseManual}
                className="flex flex-col items-center justify-center gap-2 p-6 rounded-xl border-2 border-purple-200 hover:border-purple-400 hover:bg-purple-50 transition-all"
                data-testid="classes-encuadre-choice-manual"
              >
                <FileText className="w-8 h-8 text-purple-500" />
                <span className="font-semibold text-slate-800">Escribir encuadre</span>
                <span className="text-xs text-slate-500 text-center">Agrega criterios con su nombre y peso</span>
              </button>
              <button
                onClick={() => setEncuadreMode('reuse')}
                className="flex flex-col items-center justify-center gap-2 p-6 rounded-xl border-2 border-purple-200 hover:border-purple-400 hover:bg-purple-50 transition-all"
                data-testid="classes-encuadre-choice-reuse"
              >
                <Copy className="w-8 h-8 text-purple-500" />
                <span className="font-semibold text-slate-800">Reutilizar encuadre</span>
                <span className="text-xs text-slate-500 text-center">Copia uno que ya tienes en otra clase</span>
              </button>
            </div>
          )}

          {encuadreMode === 'reuse' && (() => {
            const reusable = getReusableEncuadres();
            return (
              <div className="py-2" data-testid="classes-encuadre-reuse-list">
                <div className="flex items-center justify-between mb-3">
                  <p className="text-sm text-slate-600">Elige un encuadre de tus otras clases. Se copiará a <strong>{selectedClass?.subject}</strong> (podrás editarlo sin afectar la clase original).</p>
                  <Button variant="ghost" size="sm" onClick={() => setEncuadreMode('choice')} data-testid="classes-encuadre-reuse-back">Volver</Button>
                </div>
                {reusable.length === 0 ? (
                  <div className="text-center py-10 text-slate-500 text-sm" data-testid="classes-encuadre-reuse-empty">
                    Aún no tienes encuadres en otras clases para reutilizar.
                  </div>
                ) : (
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 max-h-[420px] overflow-y-auto pr-1">
                    {reusable.map((item, idx) => (
                      <div key={idx} className="border-2 border-slate-200 rounded-xl p-3 flex flex-col gap-2 hover:border-purple-300 transition-all" data-testid={`classes-encuadre-reuse-item-${idx}`}>
                        <div className="flex items-center gap-2">
                          <span className="inline-flex items-center gap-1 text-xs font-semibold text-purple-600"><FileText className="w-3.5 h-3.5" /> Encuadre</span>
                          <span className="text-[11px] text-slate-400 truncate" title={item.origins.join(', ')}>de: {item.origins.join(', ')}</span>
                        </div>
                        <div className="bg-slate-50 rounded-lg p-2 max-h-28 overflow-y-auto">
                          {item.criterios.map((c, ci) => (
                            <div key={ci} className="flex items-center justify-between text-xs text-slate-700 py-0.5 border-b border-slate-100 last:border-0">
                              <span className="truncate">{c.nombre}</span>
                              <span className="font-semibold text-slate-500 ml-2">{String(c.peso).replace('%', '')}%</span>
                            </div>
                          ))}
                        </div>
                        <Button
                          onClick={() => applyReuseManual(item.criterios)}
                          disabled={reuseSaving}
                          className="bg-purple-500 hover:bg-purple-600 text-white h-8 text-sm"
                          data-testid={`classes-encuadre-reuse-apply-${idx}`}
                        >
                          {reuseSaving ? 'Aplicando...' : 'Usar este'}
                        </Button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            );
          })()}

          {encuadreMode === 'manual' && (
            <div className="space-y-4">
              <div className="flex flex-col sm:flex-row gap-2">
                <input
                  value={critNombre}
                  onChange={(e) => setCritNombre(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addCriterio(); } }}
                  placeholder="Nombre del criterio (ej. Examen)"
                  className="flex-1 h-9 rounded-md border border-slate-300 px-3 text-sm focus:outline-none focus:ring-1 focus:ring-purple-400"
                  data-testid="classes-criterio-nombre-input"
                />
                <input
                  value={critPeso}
                  onChange={(e) => setCritPeso(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addCriterio(); } }}
                  placeholder="Peso % (ej. 30)"
                  className="sm:w-36 h-9 rounded-md border border-slate-300 px-3 text-sm focus:outline-none focus:ring-1 focus:ring-purple-400"
                  data-testid="classes-criterio-peso-input"
                />
                <Button onClick={addCriterio} className="bg-purple-500 hover:bg-purple-600" data-testid="classes-add-criterio-btn">
                  <Plus className="w-4 h-4 mr-1" /> Agregar criterio
                </Button>
              </div>

              {manualCriterios.length > 0 ? (
                <div className="border border-slate-200 rounded-lg overflow-hidden" data-testid="classes-criterios-list">
                  <div className="flex items-center justify-between bg-slate-50 px-3 py-2 text-xs font-semibold text-slate-600">
                    <span>Criterio</span><span>Peso</span>
                  </div>
                  <div className="divide-y divide-slate-100">
                    {manualCriterios.map((c, idx) => (
                      <div key={idx} className="flex items-center justify-between px-3 py-2 text-sm">
                        <span className="font-medium text-slate-800">{c.nombre}</span>
                        <div className="flex items-center gap-3">
                          <span className="text-purple-700 font-semibold">
                            {c.peso ? (String(c.peso).includes('%') ? c.peso : `${c.peso}%`) : '—'}
                          </span>
                          <button onClick={() => removeCriterio(idx)} className="text-red-500 hover:text-red-700" data-testid={`classes-remove-criterio-${idx}`}>
                            <X className="w-4 h-4" />
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                  <div className="flex items-center justify-between bg-slate-50 px-3 py-2 text-sm font-semibold">
                    <span className="text-slate-700">Total</span>
                    {(() => {
                      const total = manualCriterios.reduce((s, c) => s + (parseFloat(String(c.peso).replace('%', '')) || 0), 0);
                      return (
                        <span className={total === 100 ? 'text-green-600' : 'text-red-600'} data-testid="classes-criterios-total">
                          {total}%{total !== 100 ? ' (debe sumar 100%)' : ' ✓'}
                        </span>
                      );
                    })()}
                  </div>
                </div>
              ) : (
                <p className="text-sm text-slate-500">Aún no has agregado criterios.</p>
              )}

              <div className="flex justify-end gap-2">
                {selectedClass?.encuadre_manual?.length > 0 && (
                  <Button variant="outline" onClick={deleteManualEncuadre} className="text-red-600 border-red-200 hover:bg-red-50" data-testid="classes-delete-manual-encuadre-btn">
                    <Trash2 className="w-4 h-4 mr-1" /> Eliminar
                  </Button>
                )}
                <Button onClick={saveManualEncuadre} disabled={savingManual} className="bg-purple-500 hover:bg-purple-600" data-testid="classes-save-manual-encuadre-btn">
                  {savingManual ? 'Guardando...' : 'Guardar encuadre'}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
      {/* Admin Notification Dialog — migrado desde el antiguo Dashboard */}
      <Dialog open={adminNotifications.length > 0} onOpenChange={() => {}}>
        <DialogContent className="bg-white" onPointerDownOutside={(e) => e.preventDefault()}>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-amber-600">
              <Megaphone className="w-5 h-5" />
              Aviso del Administrador
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
              <p className="text-slate-700">{adminNotifications[currentAdminNotifIndex]?.mensaje}</p>
            </div>
            <p className="text-xs text-slate-400">
              Enviado por: {adminNotifications[currentAdminNotifIndex]?.created_by_name} | {adminNotifications[currentAdminNotifIndex]?.created_at ? new Date(adminNotifications[currentAdminNotifIndex].created_at).toLocaleDateString('es-MX') : ''}
            </p>
            {adminNotifications.length > 1 && (
              <p className="text-xs text-slate-500">Notificación {currentAdminNotifIndex + 1} de {adminNotifications.length}</p>
            )}
            <Button onClick={markAdminNotifRead} className="w-full bg-amber-500 hover:bg-amber-600 text-white" data-testid="mark-admin-notif-read">
              Entendido
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </Layout>
  );
};

export default ClassesPage;